// Daily streak, proven by meal photos. Any day with ≥1 photo-logged meal counts.
// Super adds freezes (auto-cover a missed day) and repair (restore within 48 h).
// The streak is DERIVED from logged days + covered days; nothing is stored that can drift.
import { addDays, daysBetween, startOfWeek } from './dates';
import type { Day, Id, MealLogEntry } from '../types';

export const MAX_FREEZES = 2;
export const COOKS_PER_WEEK_FOR_FREEZE = 3;
export const REPAIR_WINDOW_DAYS = 2;

export type StreakInfo = {
  current: number;
  best: number;
  /** A meal is already logged today. */
  todayDone: boolean;
  /** Streak continues if you cook today (yesterday was active or covered). */
  alive: boolean;
};

/**
 * `active` = days with a logged meal, `covered` = days saved by a freeze or repair.
 * Covered days keep the chain intact but don't add to the count (Duolingo-style).
 */
export function computeStreak(active: Set<Day>, covered: Set<Day>, today: Day): StreakInfo {
  const todayDone = active.has(today);
  const linked = (d: Day) => active.has(d) || covered.has(d);

  // Current: walk back from today (or yesterday if today isn't logged yet).
  let current = 0;
  let cursor = todayDone ? today : addDays(today, -1);
  while (linked(cursor)) {
    if (active.has(cursor)) current += 1;
    cursor = addDays(cursor, -1);
  }

  // Best: longest chain anywhere in history.
  const days = [...new Set([...active, ...covered])].sort();
  let best = 0;
  let run = 0;
  let prev: Day | null = null;
  for (const d of days) {
    run = prev && daysBetween(prev, d) === 1 ? run : 0;
    if (active.has(d)) run += 1;
    best = Math.max(best, run);
    prev = d;
  }

  return { current, best: Math.max(best, current), todayDone, alive: current > 0 };
}

export function activeDays(log: MealLogEntry[]): Set<Day> {
  return new Set(log.map(e => e.day));
}

/** Missed days between the last linked day before today and yesterday (oldest first). */
export function missedDays(active: Set<Day>, covered: Set<Day>, today: Day): Day[] {
  const all = [...active, ...covered].filter(d => d < today).sort();
  const last = all[all.length - 1];
  if (!last) return [];
  const gap = daysBetween(last, today) - 1;
  return Array.from({ length: Math.max(0, gap) }, (_, i) => addDays(last, i + 1));
}

/**
 * Super: on app open, spend banked freezes on missed days — only if they cover the WHOLE
 * gap (a partial cover wouldn't save the streak, so freezes aren't wasted).
 */
export function applyFreezes(
  active: Set<Day>,
  covered: Set<Day>,
  freezes: number,
  today: Day,
): { covered: Set<Day>; freezes: number; used: Day[] } {
  const missed = missedDays(active, covered, today);
  if (missed.length === 0 || missed.length > freezes) return { covered, freezes, used: [] };
  return { covered: new Set([...covered, ...missed]), freezes: freezes - missed.length, used: missed };
}

/** Super: a broken streak can be repaired if it broke at most REPAIR_WINDOW_DAYS ago. */
export function repairableDays(active: Set<Day>, covered: Set<Day>, today: Day): Day[] {
  const missed = missedDays(active, covered, today);
  return missed.length > 0 && missed.length <= REPAIR_WINDOW_DAYS ? missed : [];
}

/** Super: one freeze per Monday-week with ≥3 cooking days, holding at most MAX_FREEZES. */
export function earnedFreezeWeeks(active: Set<Day>): Day[] {
  const perWeek = new Map<Day, number>();
  for (const d of active) {
    const w = startOfWeek(d);
    perWeek.set(w, (perWeek.get(w) ?? 0) + 1);
  }
  return [...perWeek.entries()].filter(([, n]) => n >= COOKS_PER_WEEK_FOR_FREEZE).map(([w]) => w).sort();
}

/** Path progress: recipes in a country cookbook with at least one photo-logged cook. */
export function cookbookProgress(recipeIds: Id[], log: MealLogEntry[]): { cooked: number; total: number } {
  const cooked = new Set(log.map(e => e.recipeId).filter(Boolean));
  return { cooked: recipeIds.filter(id => cooked.has(id)).length, total: recipeIds.length };
}
